import { z } from 'zod';
import { ApiError, type ApiErrorKind, type FieldError } from './errors.js';

/**
 * The only place in the tree that calls `fetch`.
 *
 * Responsibilities: attach the correlation id, enforce a deadline, normalise
 * every failure into `ApiError`, and validate the success body against the
 * caller's schema so a contract drift surfaces here rather than three
 * components deep.
 */

export const CORRELATION_HEADER = 'x-correlation-id';

const DEFAULT_TIMEOUT_MS = 10_000;

/** Shape the API uses for every error response. */
const errorEnvelope = z.object({
  error: z.object({
    message: z.string(),
    fields: z.array(z.object({ field: z.string(), message: z.string() })).optional(),
  }),
});

export interface HttpClientOptions {
  /** Absolute or root-relative base, e.g. `/api` or `https://host/api`. */
  baseUrl: string;
  timeoutMs?: number;
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Called per request; defaults to `crypto.randomUUID()`. */
  correlationIdFactory?: () => string;
}

export interface RequestOptions<TSchema extends z.ZodType> {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  schema: TSchema;
  body?: unknown;
  signal?: AbortSignal;
}

export interface HttpClient {
  request<TSchema extends z.ZodType>(options: RequestOptions<TSchema>): Promise<z.infer<TSchema>>;
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

/**
 * `Retry-After` is either delta-seconds or an HTTP date. Anything else, or a
 * date in the past, yields undefined rather than a misleading zero.
 */
function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined;

  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;

  const timestamp = Date.parse(header);
  if (Number.isNaN(timestamp)) return undefined;

  const delta = Math.ceil((timestamp - Date.now()) / 1000);
  return delta > 0 ? delta : undefined;
}

async function readErrorBody(
  response: Response,
): Promise<{ message: string; fields: readonly FieldError[] }> {
  try {
    const parsed = errorEnvelope.safeParse(await response.json());
    if (parsed.success) {
      return { message: parsed.data.error.message, fields: parsed.data.error.fields ?? [] };
    }
  } catch {
    // Body was absent, truncated or not JSON — fall through to the status text.
  }
  return {
    message: response.statusText || `request failed with status ${response.status}`,
    fields: [],
  };
}

/**
 * A caller abort and an elapsed deadline are different events for the UI: one
 * is expected, the other is worth reporting.
 */
function transportError(
  cause: unknown,
  path: string,
  timeoutMs: number,
  correlationId: string,
): ApiError {
  const name = cause instanceof Error ? cause.name : '';
  const timedOut = name === 'TimeoutError';
  const aborted = name === 'AbortError';

  const message = timedOut
    ? `request to ${path} exceeded ${timeoutMs}ms`
    : aborted
      ? `request to ${path} was aborted`
      : `request to ${path} could not be completed`;

  return new ApiError({
    kind: timedOut || aborted ? 'timeout' : 'network',
    message,
    correlationId,
    cause,
  });
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 422) return 'validation';
  if (status === 429) return 'rate_limited';
  return status >= 500 ? 'server' : 'client';
}

async function responseError(response: Response, correlationId: string): Promise<ApiError> {
  const { message, fields } = await readErrorBody(response);
  const kind = kindForStatus(response.status);

  return new ApiError({
    kind,
    message,
    status: response.status,
    fields,
    correlationId: response.headers.get(CORRELATION_HEADER) ?? correlationId,
    ...(kind === 'rate_limited'
      ? { retryAfterSeconds: parseRetryAfter(response.headers.get('retry-after')) }
      : {}),
  });
}

/**
 * A 2xx that does not match the caller's schema is a contract drift, and is
 * reported here rather than surfacing as `undefined` three components deep.
 */
async function parseBody<TSchema extends z.ZodType>(
  response: Response,
  schema: TSchema,
  path: string,
  correlationId: string,
): Promise<z.infer<TSchema>> {
  let payload: unknown;
  try {
    // 204 carries no body; calling .json() on it throws.
    payload = response.status === 204 ? null : await response.json();
  } catch (cause) {
    throw new ApiError({
      kind: 'malformed',
      message: `response from ${path} was not valid JSON`,
      status: response.status,
      correlationId,
      cause,
    });
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError({
      kind: 'malformed',
      message: `response from ${path} did not match the expected contract`,
      status: response.status,
      correlationId,
      cause: parsed.error,
    });
  }

  return parsed.data;
}

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const {
    baseUrl,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    fetchImpl = globalThis.fetch,
    correlationIdFactory = () => crypto.randomUUID(),
  } = options;

  if (typeof fetchImpl !== 'function') {
    throw new TypeError('createHttpClient requires a fetch implementation');
  }

  return {
    async request({ method, path, schema, body, signal }) {
      const correlationId = correlationIdFactory();
      const timeout = AbortSignal.timeout(timeoutMs);
      // Combine so a caller abort and the deadline both cancel the request,
      // and neither leaks a listener once the other fires.
      const abortSignal = signal ? AbortSignal.any([signal, timeout]) : timeout;

      let response: Response;
      try {
        response = await fetchImpl(joinUrl(baseUrl, path), {
          method,
          signal: abortSignal,
          headers: {
            accept: 'application/json',
            [CORRELATION_HEADER]: correlationId,
            ...(body === undefined ? {} : { 'content-type': 'application/json' }),
          },
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        });
      } catch (cause) {
        throw transportError(cause, path, timeoutMs, correlationId);
      }

      if (!response.ok) {
        throw await responseError(response, correlationId);
      }

      return parseBody(response, schema, path, correlationId);
    },
  };
}
